/**
 * ScheduleBlockDialog — URS-F252
 *
 * Crea un bloqueo GLOBAL en la agenda (study_id = null en backend), es decir,
 * que impide crear citas de CUALQUIER estudio (y Qsalud) en ese rango horario.
 * 1 dia por bloqueo. Si el usuario marca "Dia completo" se rellena 08:00-21:00.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Lock } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScheduleBlockDialog({ open, onClose, defaultDate, defaultStartTime, defaultEndTime, lang, onCreated }) {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('09:15');
  const [allDay, setAllDay] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(defaultDate || '');
      setStartTime(defaultStartTime || '09:00');
      setEndTime(defaultEndTime || '09:15');
      setAllDay(false);
      setReason('');
    }
  }, [open, defaultDate, defaultStartTime, defaultEndTime]);

  const lb = lang === 'es'
    ? { title: 'Bloquear hueco', subtitle: 'Este bloqueo impedira crear citas de cualquier estudio (incluido Qsalud) en el rango indicado.', date: 'Fecha', start: 'Hora inicio', end: 'Hora fin', all_day: 'Dia completo (08:00 - 21:00)', reason: 'Motivo', reason_ph: 'Ej: Vacaciones, reunion con sponsor, mantenimiento de sala...', save: 'Bloquear', cancel: 'Cancelar', created: 'Hueco bloqueado correctamente', conflicts_title: 'Citas existentes en ese rango', conflicts_note: 'NO se han eliminado. Deberas reprogramarlas o cancelarlas manualmente.', err_required: 'Fecha, hora inicio, hora fin y motivo son obligatorios.', err_range: 'La hora de inicio debe ser anterior a la hora de fin.' }
    : { title: 'Block slot', subtitle: 'This block will prevent creating any study appointment (including Qsalud) in the specified range.', date: 'Date', start: 'Start time', end: 'End time', all_day: 'Whole day (08:00 - 21:00)', reason: 'Reason', reason_ph: 'E.g.: Holidays, sponsor meeting, room maintenance...', save: 'Block', cancel: 'Cancel', created: 'Slot blocked successfully', conflicts_title: 'Existing appointments in that range', conflicts_note: 'NOT deleted. You must reschedule or cancel them manually.', err_required: 'Date, start, end and reason are required.', err_range: 'Start time must be earlier than end time.' };

  const handleAllDay = (v) => {
    setAllDay(v);
    if (v) { setStartTime('08:00'); setEndTime('21:00'); }
  };

  const handleSave = async () => {
    if (!date || !startTime || !endTime || !reason.trim()) { toast.error(lb.err_required); return; }
    if (startTime >= endTime) { toast.error(lb.err_range); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/schedule/blocks`, {
        study_id: null, // GLOBAL
        date,
        start_time: startTime,
        end_time: endTime,
        reason: reason.trim(),
      }, { withCredentials: true });
      const conflicts = data?.conflicts || [];
      if (conflicts.length > 0) {
        toast.warning(`${lb.created} - ${conflicts.length} ${lb.conflicts_title.toLowerCase()}`);
      } else {
        toast.success(lb.created);
      }
      if (onCreated) onCreated(data?.block || null, conflicts);
      onClose();
    } catch (err) {
      toast.error(errText(err, 'Error'));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="schedule-block-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Lock size={18} className="text-red-600" weight="duotone" />
            {lb.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-stone-600 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
            {lb.subtitle}
          </p>
          <div>
            <Label className="text-xs">{lb.date}</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="block-date-input"
              className="h-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={allDay} onCheckedChange={handleAllDay} data-testid="block-all-day-switch" id="block-all-day" />
            <Label htmlFor="block-all-day" className="text-xs cursor-pointer">{lb.all_day}</Label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{lb.start}</Label>
              <Input
                type="time"
                value={startTime}
                step={300}
                disabled={allDay}
                onChange={(e) => setStartTime(e.target.value)}
                data-testid="block-start-time-input"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">{lb.end}</Label>
              <Input
                type="time"
                value={endTime}
                step={300}
                disabled={allDay}
                onChange={(e) => setEndTime(e.target.value)}
                data-testid="block-end-time-input"
                className="h-9"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">{lb.reason} <span className="text-red-500">*</span></Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={lb.reason_ph}
              rows={3}
              data-testid="block-reason-input"
              className="text-sm"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="block-cancel-btn">{lb.cancel}</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="block-save-btn"
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm gap-1"
          >
            <Lock size={14} />{saving ? '...' : lb.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
